import React, { useEffect, useMemo, useState } from "react";
import Ticket from "../components/Ticket";
import { touchItems } from "../constants/const";
import ThreeDCarousel from "../components/ThreeDCarousel";
import { useTonAddress } from "@tonconnect/ui-react";
import {
    getPlayerTicketsInLevel,
    getCycle,
} from "../blockchain/lotteryClient";
import { Spin } from "antd";
import { fromNano } from "@ton/core";
import { getLevelPriceNano } from "../blockchain/lotteryClient";

function Profile() {
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ticketsByLevel, setTicketsByLevel] = useState<
        Record<number, { jackpot: any[]; mega: any[] }>
    >({
        0: { jackpot: [], mega: [] },
        1: { jackpot: [], mega: [] },
        2: { jackpot: [], mega: [] },
    });

    const [ticketCounts, setTicketCounts] = useState<
        Record<number, { jackpot: number; mega: number }>
    >({
        0: { jackpot: 0, mega: 0 },
        1: { jackpot: 0, mega: 0 },
        2: { jackpot: 0, mega: 0 },
    });

    const [centerIndex, setCenterIndex] = useState(0);

    const LEVEL_BY_INDEX: Record<number, number> = {
        0: 0,
        1: 1,
        2: 2,
    };

    const address = useTonAddress();
    const currentLevel = LEVEL_BY_INDEX[centerIndex];

    // =========================
    // LOAD DATA
    // =========================
    useEffect(() => {
        if (!address) return;

        let cancelled = false;
        setLoading(true);

        (async () => {
            try {
                const nextTicketsByLevel: Record<number, { jackpot: any[]; mega: any[] }> = {
                    0: { jackpot: [], mega: [] },
                    1: { jackpot: [], mega: [] },
                    2: { jackpot: [], mega: [] },
                };

                const nextCounts: Record<number, { jackpot: number; mega: number }> = {
                    0: { jackpot: 0, mega: 0 },
                    1: { jackpot: 0, mega: 0 },
                    2: { jackpot: 0, mega: 0 },
                };

                for (let level = 0; level <= 2; level++) {
                    const [ticketsInfo, cycle, levelPriceNano] = await Promise.all([
                        getPlayerTicketsInLevel(address, level),
                        getCycle(level),
                        getLevelPriceNano(level),
                    ]);

                    if (!ticketsInfo || cancelled) continue;

                    // ✅ SAFE parse
                    const count = parseInt((ticketsInfo.count ?? 0n).toString(), 10);
                    const playerCycleId = parseInt((ticketsInfo.cycleId ?? 0n).toString(), 10);
                    const currentCycleId = parseInt((cycle?.id ?? 0n).toString(), 10);

                    const price = Number(fromNano(levelPriceNano ?? 0n));

                    const isActiveCycle =
                        playerCycleId === currentCycleId && cycle && !cycle.isFinished;

                    const tickets = Array.from({ length: count }, (_, i) => ({
                        ticket_number: `${playerCycleId}-${i + 1}`,
                        is_active: isActiveCycle,
                        wallet_address: address,
                        level,
                        cycleId: playerCycleId,
                        price,
                    }));

                    nextTicketsByLevel[level] = { jackpot: tickets, mega: [] };
                    nextCounts[level] = { jackpot: count, mega: 0 };
                }

                if (cancelled) return;

                setTicketsByLevel(nextTicketsByLevel);
                setTicketCounts(nextCounts);
            } catch (e) {
                console.error("Failed to load tickets", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [address]);

    // =========================
    // DERIVED DATA
    // =========================
    const currentBucket = ticketsByLevel[currentLevel];

    const currentTickets = useMemo(() => {
        return isActive
            ? currentBucket.mega
            : currentBucket.jackpot;
    }, [currentBucket, isActive]);

    const activeTickets = useMemo(() => {
        return currentTickets.filter(t => t.is_active);
    }, [currentTickets]);

    const inActiveTickets = useMemo(() => {
        return currentTickets.filter(t => !t.is_active);
    }, [currentTickets]);

    // =========================
    // HANDLERS
    // =========================
    const handleClickJackpot = () => {
        setIsActive(false);
    };

    const handleClickMegaJackpot = () => {
        setIsActive(true);
    };


    const carouselItems = useMemo(() => {
        return touchItems.map((item, idx) => {
            const level = LEVEL_BY_INDEX[idx];
            const jackpot = ticketCounts[level]?.jackpot ?? 0;
            const mega = ticketCounts[level]?.mega ?? 0;
            const total = jackpot + mega;

            return {
                ...item,
                subtitle: `${total} билетов`,
            };
        });
    }, [ticketCounts]);

    return (
        <>
            {loading && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(11, 20, 48, 0.85)",
                        backdropFilter: "blur(4px)",
                    }}
                >
                    <Spin size="large" tip="Загрузка билетов..." />
                </div>
            )}

            <div className="profile">
                <div className="profile_header">
                    Мои билеты
                </div>

                <ThreeDCarousel
                    items={carouselItems}
                    centerIndex={centerIndex}
                    setCenterIndex={setCenterIndex}
                />

                <div className="profile_switch-btn-group">
                    <div
                        className={isActive ? "profile_switch-btn-group__btn" : "profile_switch-btn-group__btn is-active-btn"}
                        onClick={handleClickJackpot}
                    >
                        <span
                            className={isActive ? "profile_switch-btn-group__btn___content" : "profile_switch-btn-group__btn___content is-active-btn"}
                        >
                            Джекпот {ticketCounts[currentLevel].jackpot}
                        </span>
                    </div>

                    <div
                        className={!isActive ? "profile_switch-btn-group__btn" : "profile_switch-btn-group__btn is-active-btn"}
                        onClick={handleClickMegaJackpot}
                    >
                        <span
                            className={!isActive ? "profile_switch-btn-group__btn___content" : "profile_switch-btn-group__btn___content is-active-btn"}
                        >
                            Мега Джекпот {ticketCounts[currentLevel].mega}
                        </span>
                    </div>
                </div>

                <div className="active-tickets">
                    <div className="active-tickets_header">
                        Активные билеты
                    </div>

                    {activeTickets.map((ticket: any) => (
                        <Ticket key={ticket.ticket_number} ticket={ticket} />
                    ))}

                    <div className="active-tickets_header">
                        Предыдущие билеты
                    </div>

                    {inActiveTickets.map((ticket: any) => (
                        <Ticket key={ticket.ticket_number} ticket={ticket} />
                    ))}
                </div>
            </div>
        </>
    );

}

export default Profile;